import { useCallback, useEffect, useRef, useState } from 'react';
import { PreviewAdminOnboardingTemplateAPI } from '../../../../api/AdminAPI';

const DEBOUNCE_MS = 600;

/** Só os campos que mudam o e-mail (mais o modo de exemplo): a prévia não depende da chave ligada. */
const previewFields = (form, mode) => ({
  key: form.key,
  subject: form.subject,
  preheader: form.preheader,
  body: form.body,
  button_label: form.plain ? '' : form.button_label,
  button_target: form.plain ? '' : form.button_target,
  plain: form.plain,
  ...(mode ? { mode } : {}),
});

/**
 * useTemplatePreview
 *
 * Prévia ao vivo do modelo em edição (valores de exemplo), atualizada um instante depois
 * de cada mudança, e envio da prévia a um e-mail digitado na hora. O modo escolhe a
 * situação de exemplo da variante (sample_modes do modelo); vazio = o padrão da API.
 *
 * @param {Object|null} form - campos do editor (null = editor fechado)
 * @param {Function} notify - (message, severity)
 * @param {string} [mode] - chave de sample_modes
 */
function useTemplatePreview(form, notify, mode = '') {
  const [preview, setPreview] = useState({ loading: false, error: null, subject: '', preheader: '', html: '' });
  const [isSendingPreview, setIsSendingPreview] = useState(false);
  const requestId = useRef(0);
  const fieldsKey = form ? JSON.stringify(previewFields(form, mode)) : '';

  useEffect(() => {
    if (!fieldsKey) return undefined;
    const id = ++requestId.current;
    setPreview((prev) => ({ ...prev, loading: true }));
    const timer = setTimeout(() => {
      PreviewAdminOnboardingTemplateAPI(JSON.parse(fieldsKey), false, (response) => {
        if (id !== requestId.current) return;
        if (!response || response.success === false) {
          setPreview((prev) => ({ ...prev, loading: false, error: response?.error || 'Não foi possível montar a prévia.' }));
          return;
        }
        setPreview({ loading: false, error: null, subject: response.subject || '', preheader: response.preheader || '', html: response.html || '' });
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [fieldsKey]);

  const sendPreview = useCallback((to) => {
    if (!form) return;
    setIsSendingPreview(true);
    PreviewAdminOnboardingTemplateAPI(previewFields(form, mode), true, (response) => {
      setIsSendingPreview(false);
      if (!response || response.success === false) {
        notify(response?.error || 'Não foi possível enviar a prévia.', 'error');
        return;
      }
      notify(`Prévia enviada para ${response.sent_to || to}.`, 'success');
    }, to);
  }, [form, mode, notify]);

  return { preview, sendPreview, isSendingPreview };
}

export default useTemplatePreview;
